import { Button, Stack, Typography } from '@mui/material'
import { useEffect } from 'react'
import { Resource, ToggleInputField } from 'utoniq-ui'

import {
  ConsoleMissionBoxFragment,
  CreateMissionInput,
  GpsData,
  GpsDataInput,
  Maybe,
  MissionRewardInput,
  MissionType,
  QuizContentInput,
} from '@/clients/utoniq-core/schema'
import { GPSMapModalProps } from '@/components/pages/ConsoleMissionCreate/template/GPSMapModal'
import { ItemSelectModalProps } from '@/components/pages/ConsoleMissionCreate/template/ItemSelectModal'
import {
  CheckInInputCell,
  DatePickerCell,
  MissionAnswerCell,
  MissionImageCell,
  RewardCell,
  TextInputCell,
} from '@/components/pages/ConsoleMissionCreate/template/MissionCreateForm'
import {
  Form,
  handleError,
  tLabel,
  useForm,
  UseFormProps,
  useMessage,
  useRouter,
  useTranslation,
} from '@/core'
import { Organism } from '@/core/architecture'

export type MissionEditFormProps = Pick<
  ItemSelectModalProps,
  'loading' | 'hasNextPage' | 'items' | 'onFetchMore' | 'onSearch' | 'artistId'
> & {
  mission: ConsoleMissionBoxFragment
  onSubmit: (values: EditMissionProps) => Promise<void>
  fileUploadInfo: FileUploadInfo
  gpsClientInfo: {
    permission: boolean
    currentLocation?: GPSMapModalProps['currentData']
  }
  onPlaceSearch: GPSMapModalProps['onSearch']
  predictions: GPSMapModalProps['predictions']
  place: GPSMapModalProps['place']
  onSelectPlace: GPSMapModalProps['onSelectPlace']
  onRequestGPSPermission: () => void
  removePlace: () => void
}

export type EditMissionProps = Omit<CreateMissionInput, 'gpsData'> & {
  hasCheckIn: boolean
  startedAt: string | null
  contentResource: Resource | null
  isGachaTicket?: boolean
  isNoQuiz?: boolean
  gpsData?: Maybe<Omit<GpsDataInput, 'radius'> & { radius: string | number }>
}

export type FileUploadInfo = {
  onDrop: () => Promise<Resource | undefined>
  progress: number
}

const schema: UseFormProps['schema'] = o => ({
  title: o.string().max(50).label(tLabel('consoleMissionEdit.title')),
  description: o
    .string()
    .max(500)
    .label(tLabel('consoleMissionEdit.description')),
  content: o.object().when('type', {
    switch: [
      {
        is: MissionType.Quiz,
        then: o.object().when('isNoQuiz', {
          is: true,
          then: o.object({
            image: o.object({
              uri: o
                .string()
                .allow('')
                .optional()
                .label(tLabel('consoleMissionEdit.content.quiz.image.uri')),
            }),
            answer: o
              .string()
              .max(50)
              .allow('')
              .optional()
              .label(tLabel('consoleMissionEdit.content.quiz.answer')),
          }),
          otherwise: o.object({
            image: o.object({
              uri: o
                .string()
                .label(tLabel('consoleMissionEdit.content.quiz.image.uri')),
            }),
            answer: o
              .string()
              .max(50)
              .label(tLabel('consoleMissionEdit.content.quiz.answer')),
          }),
        }),
      },
      {
        is: MissionType.ItemCollect,
        then: o.object({
          items: o.array().items({
            itemId: o.string(),
            requiredCount: o.number(),
          }),
        }),
      },
    ],
    otherwise: o.object().allow(null).optional(),
  }),
  startedAt: o
    .date()
    .allow(null)
    .optional()
    .label(tLabel('consoleMissionEdit.startedAt')),
  expiredAt: o
    .date()
    .allow(null)
    .optional()
    .label(tLabel('consoleMissionEdit.expiredAt')),
  type: o.string(),
  isPublished: o.boolean(),
  //check mission have the GachaTicket
  isGachaTicket: o.boolean(),
  reward: o.object().when('isGachaTicket', {
    is: true,
    then: o.object({
      item: o
        .object({
          id: o.string().label(tLabel('consoleMissionEdit.reward.item.id')),
        })
        .allow(null)
        .optional(),
      gachaTicket: o.object({
        gachaTicketId: o.string(),
        count: o.number(),
      }),
    }),
    otherwise: o.object({
      item: o.object({
        id: o.string().label(tLabel('consoleMissionEdit.reward.item.id')),
      }),
      gachaTicket: o
        .object({
          gachaTicketId: o.string(),
          count: o.number(),
        })
        .allow(null)
        .optional(),
    }),
  }),
  gpsData: o.object().when('hasCheckIn', {
    is: true,
    then: o.object({
      latitude: o.number().label(tLabel('consoleMissionEdit.gpsData.latitude')),
      longitude: o
        .number()
        .label(tLabel('consoleMissionEdit.gpsData.longitude')),
      approximateAddress: o
        .string()
        .label(tLabel('consoleMissionEdit.gpsData.approximateAddress')),
      radius: o.string().label(tLabel('consoleMissionEdit.gpsData.radius')),
    }),
    otherwise: o.object().allow(null).optional(),
  }),
  //local field
  isNoQuiz: o.boolean().optional(),
  contentResource: o.object().allow(null).optional(),
  hasCheckIn: o.boolean().allow(false).optional(),
})

export const MissionEditForm: Organism<MissionEditFormProps> = ({
  mission,
  onSubmit,
  fileUploadInfo,
  loading,
  items,
  hasNextPage,
  artistId,
  onSearch,
  onPlaceSearch,
  predictions,
  place,
  onSelectPlace,
  onFetchMore,
  gpsClientInfo,
  onRequestGPSPermission,
  removePlace,
}) => {
  const { t } = useTranslation('consoleMissionEdit')
  const { router, routerPages } = useRouter()
  const { showError } = useMessage()
  const {
    handleSubmit,
    formState: { isSubmitting, errors, isValid, isDirty },
    register,
    setValue,
    reset,
    watch,
    setError,
    trigger,
  } = useForm<EditMissionProps>({
    schema,
    defaultValues: {
      ...defaultValues,
    },
  })
  const { ref: isNoQuizRef, ...restIsNoQuiz } = register('isNoQuiz')
  const isNoQuizWatch = watch('isNoQuiz')

  useEffect(() => {
    const content = buildContent(mission)
    reset({
      title: mission.title,
      description: mission.description,
      type: mission.type,
      isPublished: mission.isPublished,
      content,
      isGachaTicket: !!mission?.reward?.gachaTicket,
      reward: buildReward(mission.reward),
      hasCheckIn: !!mission.gpsData,
      gpsData: buildGpsData(mission.gpsData),
      startedAt: mission.startedAt,
      expiredAt: mission.expiredAt,
      isNoQuiz: !(content.answer && content.image?.uri),
      contentResource: {
        id: '',
        resourceName: '',
        imageUrl: mission.content.image?.uri ?? '',
        imageUploadPath: '',
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mission])

  const onHandleSubmit = handleSubmit(
    ({ hasCheckIn, gpsData, content, isNoQuiz, ...values }) => {
      if (!isDirty) {
        router.goBack(routerPages.ConsoleMissions)
        return
      }
      return onSubmit({
        ...values,
        hasCheckIn,
        gpsData: hasCheckIn ? gpsData : null,
        content: {
          ...content,
          ...(isNoQuiz ? getContentNoQuiz(values.type) : {}),
        },
      }).catch(error => {
        handleError<EditMissionProps>(error, setError, showError)
      })
    }
  )

  return (
    <Form sx={{ p: 5 }}>
      <TextInputCell type="text" {...register('title')} error={errors.title} />
      <TextInputCell
        type="textarea"
        textFieldProps={{
          multiline: true,
          rows: 5,
          InputProps: { sx: { px: 4, py: 3 } },
        }}
        {...register('description')}
        error={errors.description}
      />
      <CheckInInputCell
        artistId={artistId}
        setValue={setValue}
        value={watch('gpsData')}
        hasCheckIn={watch('hasCheckIn')}
        gpsClientInfo={gpsClientInfo}
        onPlaceSearch={onPlaceSearch}
        predictions={predictions}
        place={place}
        onSelectPlace={onSelectPlace}
        onRequestGPSPermission={onRequestGPSPermission}
        removePlace={removePlace}
        inputProps={{
          type: 'text',
          error: errors.gpsData?.radius,
          mask: '00000m',
          ...register('gpsData.radius'),
        }}
        inputApproximateAddressProps={{
          type: 'text',
          error: errors.gpsData?.approximateAddress,
          ...register('gpsData.approximateAddress'),
        }}
      />
      {mission.type === MissionType.Quiz && (
        <>
          <ToggleInputField
            content={{
              title: t('noQuizTitle'),
              description: (
                <Typography variant="body2" color="text.secondary">
                  {t('noQuizNote')}
                </Typography>
              ),
            }}
            checked={isNoQuizWatch}
            inputRef={isNoQuizRef}
            sx={{ mx: -2, mt: 4, mb: -5 }}
            {...restIsNoQuiz}
            onChange={async event => {
              setValue('isNoQuiz', event.target.checked, {
                shouldValidate: true,
                shouldDirty: true,
              })
              await trigger(['content.answer', 'content.image.uri'])
            }}
          />
          <MissionImageCell
            watch={watch}
            disabled={isNoQuizWatch}
            setValue={setValue}
            error={errors.content?.image?.uri}
            fileUploadInfo={fileUploadInfo}
          />
          <MissionAnswerCell
            inputProps={{
              type: 'text',
              disabled: isNoQuizWatch,
              error: errors.content?.answer,
              ...register('content.answer'),
            }}
          />
        </>
      )}
      {!watch('isGachaTicket') && (
        <RewardCell
          items={items}
          artistId={artistId}
          value={watch('reward.item.id')}
          defaultItem={mission.reward.item?.item ?? undefined}
          loading={loading}
          setValue={setValue}
          hasNextPage={hasNextPage}
          onFetchMore={onFetchMore}
          onSearch={onSearch}
        />
      )}
      <DatePickerCell
        name="startedAt"
        error={errors.startedAt}
        setValue={setValue}
        value={watch('startedAt')}
      />
      <DatePickerCell
        name="expiredAt"
        error={errors.expiredAt}
        setValue={setValue}
        value={watch('expiredAt')}
      />
      <Stack spacing={5} alignItems="center">
        <Button
          size="large"
          sx={{ width: 244 }}
          disabled={!isValid || isSubmitting || !!fileUploadInfo.progress}
          loading={isSubmitting || !!fileUploadInfo.progress}
          onClick={onHandleSubmit}
        >
          {t('edit')}
        </Button>
        {/**
         * @description
         * Open when function is implemented in the future
         * https://app.clickup.com/t/908127/UT-5944
         */}
        {/*<Button*/}
        {/*  variant="outlined"*/}
        {/*  size="large"*/}
        {/*  sx={{ width: 244 }}*/}
        {/*  disabled={!isValid || isSubmitting}*/}
        {/*>*/}
        {/*  {t('temporarilySave')}*/}
        {/*</Button>*/}
      </Stack>
    </Form>
  )
}

const defaultValues: EditMissionProps = {
  title: '',
  description: '',
  type: MissionType.Quiz,
  content: {
    answer: '',
    image: {
      uri: '',
    },
  },
  contentResource: null,
  gpsData: {
    longitude: 0,
    latitude: 0,
    radius: '',
    approximateAddress: '',
  },
  isPublished: true,
  expiredAt: null,
  reward: {
    item: {
      id: '',
    },
    gachaTicket: null,
  },
  isNoQuiz: true,
  hasCheckIn: false,
  startedAt: null,
}

function buildGpsData(
  gpsData?: GpsData | null
): (Omit<GpsDataInput, 'radius'> & { radius: string | number }) | null {
  if (!gpsData) return null
  return {
    approximateAddress: gpsData.approximateAddress,
    latitude: gpsData.latitude,
    longitude: gpsData.longitude,
    radius: String(gpsData.radius ?? 0),
  }
}

function buildReward(reward?: MissionRewardInput): MissionRewardInput {
  const rewardInput: MissionRewardInput = {
    item: null,
    gachaTicket: null,
  }
  if (reward?.item) {
    rewardInput.item = { id: reward.item.id }
  }
  if (reward?.gachaTicket) {
    rewardInput.gachaTicket = {
      gachaTicketId: reward.gachaTicket.gachaTicketId,
      count: reward.gachaTicket.count,
    }
  }
  return rewardInput
}

function buildContent(mission: ConsoleMissionBoxFragment): QuizContentInput {
  if ('answer' in mission.content) {
    return {
      answer: mission.content.answer,
      image: {
        uri: mission.content.image?.uri ?? '',
      },
    }
  }
  if ('items' in mission.content) {
    return {
      items: mission.content.items?.map(({ itemId, requiredCount }) => ({
        itemId,
        requiredCount,
      })),
    }
  }
  return {}
}

function getContentNoQuiz(missionType: MissionType) {
  switch (missionType) {
    case MissionType.Quiz:
      return {
        answer: '',
        image: { uri: '' },
      }
    default:
      return {}
  }
}
